import React, { createRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Tabs, Icon, Button } from 'shared/components';

import MediaGallery from 'modules/appointmentInfo/components/MediaGallery';

import AppointmentDetails from '../AppointmentDetails';
import ConversationsTab from '../ConversationsTab';
import theme from './InfoTab.scss';

const propTypes = {
  intl: PropTypes.object.isRequired,
  infoTabId: PropTypes.string,
  changePatientInfoTab: PropTypes.func.isRequired,
  rtl: PropTypes.bool.isRequired,
  acceptedFilesExtensions: PropTypes.string.isRequired,
  isFABEnabled: PropTypes.bool.isRequired,
  isMissingFiles: PropTypes.bool.isRequired,
  galleryEnabled: PropTypes.bool.isRequired,
  isMaxUploadsReached: PropTypes.bool.isRequired,
  openMediaGalleryTab: PropTypes.func.isRequired,
  selectAndStartUploadMedia: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    canSeeConversation: PropTypes.bool.isRequired,
  }).isRequired,
  missingFilesMessage: PropTypes.string,
  maxFilesReachedMessage: PropTypes.string,
  missingCaseMessage: PropTypes.string,
};

const defaultProps = {
  infoTabId: '1',
  missingCaseMessage: null,
  missingFilesMessage: null,
  maxFilesReachedMessage: null,
};

const InfoTab = ({
  infoTabId,
  changePatientInfoTab,
  intl,
  rtl,
  isFABEnabled,
  acceptedFilesExtensions,
  openMediaGalleryTab,
  selectAndStartUploadMedia,
  isMaxUploadsReached,
  galleryEnabled,
  missingFilesMessage,
  missingCaseMessage,
  isMissingFiles,
  permissions,
  maxFilesReachedMessage,
}) => {
  const tabUploadRef = createRef();
  const tabUploadRef2 = createRef();
  const handleUploadBtnClick = (e, inputRef) => {
    if (isMaxUploadsReached) return;
    if (e.key === 'Enter') {
      if (inputRef === tabUploadRef) openMediaGalleryTab();
      inputRef.current.click();
    }
  };

  return (
    <Tabs
      selectedKey={ infoTabId }
      onSelect={ (key) => changePatientInfoTab(key) }
      rtl={ rtl }
      list={ [{
        key: '1',
        testId: 'appointment-tab-details',
        ariaLabel: intl.formatMessage({
          id: 'Appointment.DetailsTabButton.TitleAttribute',
          defaultMessage: '** Appointment details',
        }),
        heading: (
          <div className={ theme.tabHeader }>
            <Icon name="appointment-info" className={ theme.icon } />
            <span className={ theme.message }>
              <FormattedMessage id="Appointment.Tabs.Details.Title" defaultMessage="** Details" />
              { missingCaseMessage &&
                <Icon
                  className={ theme.missingFilesIcon }
                  name="exclamation_outline"
                />
              }
            </span>
          </div>
        ),
        content: (
          <Fragment>
            <AppointmentDetails style={{ visibility: infoTabId === '1' ? 'visible' : 'hidden' }} />
            { isFABEnabled && (
              <Button
                tabIndex={ infoTabId === '1' ? 0 : -1 }
                ariaLabel={ intl.formatMessage({
                  id: 'Appointment.UploadFileButton.TitleAttribute',
                  defaultMessage: '** Upload file',
                }) }
                testId="tab1-upload-file-button"
                buttonType="circle"
                iconName="naked-x"
                className={ theme.floatingActionButton }
                onKeyPress={ (e) => handleUploadBtnClick(e, tabUploadRef) }
                size="large"
                disabled={ isMaxUploadsReached }
              >
                <If condition={ !isMaxUploadsReached }>
                  <input
                    type="file"
                    ref={ tabUploadRef }
                    tabIndex="-1"
                    accept={ acceptedFilesExtensions }
                    onClick={ (e) => {
                      if (isMaxUploadsReached) return e.preventDefault();
                      e.target.value = null;
                      openMediaGalleryTab();
                    } }
                    onChange={ (e) => selectAndStartUploadMedia(e.target.files) }
                    style={{
                      top: 0,
                      right: 0,
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                    }}
                  />
                </If>
              </Button>
            ) }
          </Fragment>
        ),
      }, {
        key: '3',
        testId: 'appointment-tab-files',
        ariaLabel: intl.formatMessage({
          id: 'Appointment.FilesTabButton.TitleAttribute',
          defaultMessage: '** Uploaded files',
        }),
        heading: (
          <div className={ theme.tabHeader }>
            <Icon name="attachment" className={ theme.icon } />
            <span className={ theme.message }>
              <FormattedMessage id="Appointment.Tabs.Files.Title" defaultMessage="** Files" />
              { isMissingFiles &&
                <Icon
                  className={ theme.missingFilesIcon }
                  name="exclamation_outline"
                />
              }
            </span>
          </div>
        ),
        content: (
          <Fragment>
            { missingFilesMessage && <div className={ theme.missingFilesMessage }> { missingFilesMessage } </div> }
            { maxFilesReachedMessage && <div className={ theme.maxUploadReachedFilesMessage }> { maxFilesReachedMessage } </div> }
            { missingCaseMessage && <div className={ theme.missingFilesMessage }> { missingCaseMessage } </div> }
            <div className={ theme.content } id="appointment-info-sidebar">
              <MediaGallery style={{ visibility: infoTabId === '3' ? 'visible' : 'hidden' }} />

              { isFABEnabled && (
                <Button
                  tabIndex={ infoTabId === '3' ? 0 : -1 }
                  ariaLabel={ intl.formatMessage({
                    id: 'Appointment.UploadFileButton.TitleAttribute',
                    defaultMessage: '** Upload file',
                  }) }
                  testId="tab3-upload-file-button"
                  buttonType="circle"
                  iconName="naked-x"
                  className={ theme.floatingActionButton }
                  onKeyPress={ (e) => handleUploadBtnClick(e, tabUploadRef2) }
                  size="large"
                  disabled={ isMaxUploadsReached }
                >
                  <If condition={ !isMaxUploadsReached } >
                    <input
                      type="file"
                      ref={ tabUploadRef2 }
                      tabIndex="-1"
                      accept={ acceptedFilesExtensions }
                      onClick={ (e) => {
                        if (isMaxUploadsReached) return e.preventDefault();
                        e.target.value = null;
                      } }
                      onChange={ (e) => selectAndStartUploadMedia(e.target.files) }
                      style={{
                        top: 0,
                        right: 0,
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                      }}
                    />
                  </If>
                </Button>
              ) }
            </div>
          </Fragment>
        ),
      }, {
        key: '2',
        testId: 'appointment-tab-conversations',
        ariaLabel: intl.formatMessage({
          id: 'Appointment.ConversationsTabButton.TitleAttributes',
          defaultMessage: '** Appointment conversations',
        }),
        heading: (
          <div className={ theme.tabHeader }>
            <Icon name="chat-bubbles" className={ theme.icon } />
            <span className={ theme.message }>
              <FormattedMessage id="Appointment.Tabs.Details.Conversations" defaultMessage="** Conversations" />
            </span>
          </div>
        ),
        content: (
          <ConversationsTab style={{ visibility: infoTabId === '2' ? 'visible' : 'hidden' }} />
        ),
      },
      ].filter(tabItem =>
        (tabItem.key !== '3' || galleryEnabled) &&
        (tabItem.key !== '2' || permissions.canSeeConversation))
      }
    />
  );
};

InfoTab.propTypes = propTypes;
InfoTab.defaultProps = defaultProps;

export default InfoTab;
